import React from 'react';
import RowDeleteButton from '../../../components/ActionButtons/RowDeleteButton';
import RowEditButton from '../../../components/ActionButtons/RowEditButton';
import RowStartButton from '../../../components/ActionButtons/RowStartButton';
import RowStopButton from '../../../components/ActionButtons/RowStopButton';
import HealthDot from '../../../components/HealthDot/HealthDot';
import { confirmDialog } from '../../../utils/ConfirmDialog';

/**
 * props:
 * - data
 * - onEditClick
 * - onStopClick
 * - onStartClick
 * - onDeleteClick
 * - onExportClick
 * - onSystemExportClick
 */
function MyStrategyRow(props) {

    function getStatusClass(isActive) {
        return isActive ? 'text-success' : 'text-danger';
    }

    function getStatusText(isActive) {
        return isActive ? 'Ativo' : 'Parado';
    }

    function getComponentBadgeClass(isActive) {
        return `badge me-1 ${isActive ? 'bg-success' : 'bg-secondary'}`;
    }

    function monitorLabel(monitor) {
        if (!monitor) return null;
        const label = monitor.interval ? `${monitor.type} ${monitor.interval}` : monitor.type;
        return label;
    }

    // Nível (preset) escolhido na criação — só rótulo. CUSTOM = parâmetros personalizados.
    const LEVEL_BADGE = {
        CONSERVATIVE: { text: '🛡️ Conservador',  cls: 'bg-success' },
        MODERATE:     { text: '⚖️ Moderado',     cls: 'bg-warning text-dark' },
        AGGRESSIVE:   { text: '🚀 Agressivo',     cls: 'bg-danger' },
        CUSTOM:       { text: '⚙️ Personalizado', cls: 'bg-secondary' },
    };
    const levelBadge = LEVEL_BADGE[props.data.riskLevel];

    const { monitor, buyAutomation, sellAutomation } = props.data;
    const hasSeparateSell = sellAutomation && sellAutomation.id !== buyAutomation?.id;

    // "Reatualizar" (rebase): AI Full single-símbolo concreto OU Automática (coringa *USDT). Detecta
    // por templateId persistido ou pelo nome ([AI Full] / [AI Full Auto]). Exclui a lista multi-ativo
    // ([AI Full] *USDT sem "Auto"), ainda não coberta.
    const _name = props.data.name || '';
    const _isWildcard = !props.data.symbol || props.data.symbol.startsWith('*');
    const _isAuto = /^\[AI Full Auto\]/.test(_name);
    const isReapplyable = (props.data.templateId === 'AI_FULL' || /^\[AI Full( Auto)?\]/.test(_name))
        && (!_isWildcard || _isAuto);

    // Estratégia movida a IA (lê :AI_SIGNAL): a rotulagem pelo sweep chama a IA sobre os candles dos
    // cenários → consome tokens e o rótulo é INDICATIVO (candles sintéticos). Avisa no botão 🎯.
    const usesAi = /AI_SIGNAL/.test(`${buyAutomation?.indexes || ''}${sellAutomation?.indexes || ''}`);

    // Orquestrador por regime (opt-in). regimeProfile.bad = regimes em que a estratégia perde no
    // teste sintético; regimeState.entriesPausedByRegime = entradas pausadas AGORA pelo orquestrador.
    const REGIME_PT = { TRENDING_UP: 'alta', TRENDING_DOWN: 'queda', RANGING: 'lateral', HIGH_VOLATILITY: 'volátil' };
    const regimeProfile = props.data.regimeProfile;
    const regimeState = props.data.regimeState;
    const orchestrated = !!props.data.regimeOrchestrated;
    const labeled = !!(regimeProfile && regimeProfile.at);          // rotulada = tem perfil da simulação
    const goodRegimes = (regimeProfile && regimeProfile.good) || []; // regimes em que LUCRA (pode ser vários)
    const badRegimes = (regimeProfile && regimeProfile.bad) || [];   // regimes em que PERDE (entradas pausam)
    const entriesPaused = !!(regimeState && regimeState.entriesPausedByRegime);
    const labeledAtStr = labeled ? new Date(regimeProfile.at).toLocaleString('pt-BR') : '';

    return (
        <tr className="align-middle">
            <td>
                <HealthDot health={props.health} />
                {props.data.symbol ? props.data.symbol : '*'}
            </td>
            <td>
                <div>
                    {props.data.name}
                    {levelBadge && (
                        <span className={`badge ms-2 ${levelBadge.cls}`} title='Nível escolhido na criação'>
                            {levelBadge.text}
                        </span>
                    )}
                </div>
                <div className='mt-1'>
                    {monitor && (
                        <span className={getComponentBadgeClass(monitor.isActive)} title='Monitor'>
                            {monitorLabel(monitor)}
                        </span>
                    )}
                    {buyAutomation && (
                        <span className={getComponentBadgeClass(buyAutomation.isActive)} title='Automação de compra'>
                            {buyAutomation.name}
                        </span>
                    )}
                    {hasSeparateSell && (
                        <span className={getComponentBadgeClass(sellAutomation.isActive)} title='Automação de venda'>
                            {sellAutomation.name}
                        </span>
                    )}
                </div>
                {(orchestrated || labeled || entriesPaused) && (
                    <div className='mt-1'>
                        {orchestrated && (
                            <span className='badge bg-info me-1' title='Esta estratégia participa do orquestrador por regime'>
                                🧭 Orquestrada por regime
                            </span>
                        )}
                        {labeled && (
                            <span className='badge bg-success me-1'
                                title={`Rotulada pela simulação em ${labeledAtStr}. Rode de novo pelo botão 🎯 para atualizar.`}>
                                ✓ Rotulada
                            </span>
                        )}
                        {goodRegimes.length > 0 && (
                            <span className='badge bg-success me-1' style={{ opacity: 0.75 }}
                                title='Regimes em que esta estratégia LUCRA na simulação (entradas liberadas nesses regimes)'>
                                Bem em: {goodRegimes.map(r => REGIME_PT[r] || r).join(', ')}
                            </span>
                        )}
                        {badRegimes.length > 0 && (
                            <span className='badge bg-secondary me-1' title='Regimes em que esta estratégia PERDE na simulação (o orquestrador pausa as entradas nesses regimes)'>
                                Ruim em: {badRegimes.map(r => REGIME_PT[r] || r).join(', ')}
                            </span>
                        )}
                        {labeled && goodRegimes.length === 0 && badRegimes.length === 0 && (
                            <span className='badge bg-light text-dark border me-1' title='A simulação não encontrou um regime claramente bom ou ruim — a estratégia opera em todos'>
                                sem regime claro
                            </span>
                        )}
                        {entriesPaused && (
                            <span className='badge bg-warning text-dark me-1' title='O orquestrador pausou as ENTRADAS por causa do regime atual — vendas e proteções seguem ativas'>
                                ⏸️ Entradas pausadas por regime
                            </span>
                        )}
                    </div>
                )}
            </td>
            <td>
                <span className={getStatusClass(props.data.isActive)}>{getStatusText(props.data.isActive)}</span>
            </td>
            <td>
                <RowEditButton id={props.data.id} title='Editar esta Estratégia' modalId='modalStrategy' onClick={props.onEditClick} />
                <RowStopButton id={props.data.id} title='Parar esta Estratégia' onClick={props.onStopClick} showButton={props.data.isActive} />
                <RowStartButton id={props.data.id} title='Iniciar esta Estratégia' onClick={props.onStartClick} showButton={!props.data.isActive} />
                <RowDeleteButton id={props.data.id} title='Excluir esta Estratégia' onClick={props.onDeleteClick} showButton={!props.data.isActive} />
                <button
                    type='button'
                    className='btn btn-outline-info btn-xs ms-2'
                    title='Simular o comportamento desta Estratégia'
                    data-bs-toggle='modal'
                    data-bs-target='#modalSimulateStrategy'
                    data-id={props.data.id}
                    onClick={props.onSimulateClick}
                >
                    <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 8l6 4-6 4V8z" />
                    </svg>
                </button>
                <button
                    type='button'
                    className='btn btn-outline-primary btn-xs ms-1'
                    title='Testar esta Estratégia (backtest histórico + simulação por regime)'
                    data-bs-toggle='modal'
                    data-bs-target='#modalTestStrategy'
                    data-id={props.data.id}
                    onClick={props.onTestClick}
                >
                    <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3h6M10 3v6.5L5.2 17.3A1.8 1.8 0 006.7 20h10.6a1.8 1.8 0 001.5-2.7L14 9.5V3M8 14h8" />
                    </svg>
                </button>
                <button
                    type='button'
                    className='btn btn-secondary btn-xs ms-1'
                    title='Analisar / Exportar esta Estratégia'
                    data-bs-toggle='modal'
                    data-bs-target='#modalExportStrategy'
                    data-id={props.data.id}
                    onClick={props.onExportClick}
                >
                    <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                </button>
                <button
                    type='button'
                    className='btn btn-outline-secondary btn-xs ms-1'
                    title='Exportar como arquivo portável (.hydra.json)'
                    data-id={props.data.id}
                    onClick={props.onSystemExportClick}
                >
                    <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                </button>
                <button
                    type='button'
                    className={`btn btn-xs ms-1 position-relative ${usesAi ? 'btn-outline-primary' : 'btn-outline-warning'}`}
                    title={usesAi
                        ? 'Rotular pela simulação por regime. Esta estratégia é movida a IA: a simulação consulta a IA sobre os candles (consome tokens) e o rótulo é INDICATIVO. Configure o provedor em Configurações → IA no backtest.'
                        : 'Rotular pela simulação (deriva em quais regimes esta estratégia perde)'}
                    data-id={props.data.id}
                    onClick={usesAi
                        ? async () => {
                            const ok = await confirmDialog({
                                title: 'Rotular estratégia de IA',
                                message: 'Esta estratégia é movida a IA. Rotular vai consultar a IA sobre os candles dos cenários (consome tokens) e o rótulo é indicativo.\n\nContinuar?',
                                confirmLabel: 'Rotular', confirmVariant: 'primary'
                            });
                            if (ok) props.onLabelRegimeClick({ target: { dataset: { id: String(props.data.id) } } });
                        }
                        : props.onLabelRegimeClick}
                    disabled={props.regimeLabeling}
                >
                    {props.regimeLabeling
                        ? <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                        : <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                          </svg>}
                    {usesAi && !props.regimeLabeling &&
                        <span className='badge bg-primary position-absolute top-0 start-100 translate-middle' style={{ fontSize: '0.5rem', padding: '2px 3px' }} title='Usa IA'>IA</span>}
                </button>
                <button
                    type='button'
                    className={`btn btn-xs ms-1 ${orchestrated ? 'btn-info' : 'btn-outline-info'}`}
                    title={orchestrated
                        ? 'Orquestrador por regime LIGADO nesta estratégia — clique para desligar (reinicia a estratégia se estiver ativa)'
                        : 'Ligar o orquestrador por regime (pausa entradas nos regimes ruins; reinicia a estratégia se estiver ativa). Requer o master ligado em Configurações.'}
                    data-id={props.data.id}
                    onClick={props.onToggleOrchestrateClick}
                    disabled={props.orchestrating}
                >
                    {props.orchestrating
                        ? <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                        : <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.5 8.5l-2 5-5 2 2-5z" />
                          </svg>}
                </button>
                {isReapplyable && (
                    <button
                        type='button'
                        className='btn btn-outline-success btn-xs ms-1'
                        title='Reatualizar: aplicar a versão mais recente do modelo AI Full nesta estratégia (preserva histórico; você confere o que muda antes)'
                        data-bs-toggle='modal'
                        data-bs-target='#modalReapplyStrategy'
                        data-id={props.data.id}
                        onClick={props.onReapplyClick}
                    >
                        <svg className="icon icon-xs" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                )}
            </td>
        </tr>
    );
}

export default MyStrategyRow;